import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ThemeContext = createContext({
  isDark: false,
  colorTheme: 'light',
  setColorTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

// Resolve whether dark mode should be active
const resolveIsDark = (theme) => {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  // system
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

// Apply or remove .dark class on <html>
const applyDarkClass = (isDark) => {
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

export const ThemeProvider = ({ children }) => {
  // Dark mode state — initialize from localStorage for instant load
  const [colorTheme, setColorThemeState] = useState(() => {
    return localStorage.getItem('color_theme') || 'light';
  });
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('color_theme') || 'light';
    return resolveIsDark(saved);
  });

  // Set color theme — saves to localStorage, updates state, applies immediately
  const setColorTheme = useCallback((theme) => {
    setColorThemeState(theme);
    localStorage.setItem('color_theme', theme);
    const dark = resolveIsDark(theme);
    setIsDark(dark);
    applyDarkClass(dark);
  }, []);

  // Apply dark class on mount
  useEffect(() => {
    applyDarkClass(isDark);
  }, [isDark]);

  // Listen for system preference changes (only matters in 'system' mode)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (colorTheme === 'system') {
        const dark = mediaQuery.matches;
        setIsDark(dark);
        applyDarkClass(dark);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [colorTheme]);

  return (
    <ThemeContext.Provider value={{ isDark, colorTheme, setColorTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
